/**
 * Shared content primitives for the long-form article areas:
 * `/tutorials` (sectioned) and `/geo` (flat).
 *
 * `next[]` is a list of absolute article paths (e.g. `/geo/what-is-mention-rate`)
 * rather than bare slugs, because a recommendation can point into either area.
 * The card's label is read from the target article's `title`, so it can never
 * drift; every path must resolve to a real article (see `article-routes.ts`).
 */

export type TutorialBlock =
  | { type: "p"; text: string }
  | { type: "h2"; text: string }
  | { type: "h3"; text: string }
  | { type: "ul"; items: string[] }
  | { type: "callout"; text: string }
  | { type: "image"; src?: string; alt?: string; caption?: string };

export type Article = {
  slug: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  category: string;
  product: string;
  difficulty: "Beginner" | "Intermediate" | "Advanced";
  estimatedTime: string;
  bestFor: string;
  intro: string;
  blocks: TutorialBlock[];
  /** Absolute paths of the "Recommended next" articles, e.g. `/geo/what-is-mention-rate`. */
  next?: string[];
};

export type ArticleSection = {
  title: string;
  articles: Article[];
};

export const p = (text: string): TutorialBlock => ({ type: "p", text });
export const h2 = (text: string): TutorialBlock => ({ type: "h2", text });
export const h3 = (text: string): TutorialBlock => ({ type: "h3", text });
export const ul = (items: string[]): TutorialBlock => ({ type: "ul", items });
export const img = (src?: string, alt?: string, caption?: string): TutorialBlock => ({
  type: "image",
  src,
  alt,
  caption,
});
